import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import moment from 'moment';
import InputStore from './InputStore';

export default class DatepickerStore {
    static DATE_FORMAT = 'MMM-DD-YYYY';

    static PropType = PropTypes.shape({
        monthTitle: PropTypes.string.isRequired,
        daysOfMonth: MobxPropTypes.arrayOrObservableArray,
        showPrevMonth: PropTypes.func.isRequired,
        showNextMonth: PropTypes.func.isRequired,
        setDay: PropTypes.func.isRequired,
        selectedDate: InputStore.PropType,
        checkDayAvailability: PropTypes.func.isRequired,
        checkMonthAvailability: PropTypes.func.isRequired,
        valueFormatted: PropTypes.func,
        setStringDate: PropTypes.func,
        allowPastDates: PropTypes.bool
    });

    constructor(dateOrOptions) {
        dateOrOptions = dateOrOptions || {};
        const opts = dateOrOptions.value ? dateOrOptions : { value: dateOrOptions };
        extendObservable(this, /** @class DatepickerStore */{
            isCalendarOpen: false,
            defaultDate: null,
            date: this.getGmtDate(opts.value),
            appliedDate: null,
            monthTitleFormat: 'MMMM YYYY',
            anchor: this.getGmtDate(opts.value).startOf('month'),
            selectedDate: new InputStore({ value: this.getGmtDate(opts.value).format(DatepickerStore.DATE_FORMAT) }),
            allowPastDates: !!opts.allowPastDates,
            format: dateOrOptions.dateFormat ? dateOrOptions.dateFormat : DatepickerStore.DATE_FORMAT,

            get monthTitle() {
                return this.anchor.format(this.monthTitleFormat);
            },

            get daysOfWeek() {
                return moment.weekdays().map((title) => title[0].toUpperCase());
            },

            get daysOfMonth() {
                return [
                    // Sunday is 0...
                    ...new Array(this.anchor.day()).fill(''),
                    ...new Array(this.anchor.daysInMonth()).fill('').map((_, i) => i + 1),
                ];
            },

            get selectedDay() {
                return this.date && this.date.isSame(this.anchor, 'month') ? this.date.date() : -1;
            },

            get formattedSelectedDate() {
                return this.getGmtDate(this.date).format(this.format);
            },

            getValue: () => this.date.format(),
        }, opts);
    }

    toggleCalendar = action(() => {
        if (!this.isCalendarOpen && this.anchor.clone().endOf('month').isSameOrBefore(this.getGmtDate())) {
            this.anchor = this.anchor.clone().add(1, 'months');
        }
        this.isCalendarOpen = !this.isCalendarOpen;
    });

    closeCalendar = action(() => {
        this.isCalendarOpen = false;
    });

    showPrevMonth = action(() => {
        this.anchor = this.anchor.clone().subtract(1, 'months');
    });

    showNextMonth = action(() => {
        this.anchor = this.anchor.clone().add(1, 'months');
    });

    setDate = action((date) => {
        if (date) {
            this.date = this.getGmtDate(date);
            this.anchor = this.date.clone().startOf('month');
            this.selectedDate.setValue(date.format(DatepickerStore.DATE_FORMAT));
        } else {
            this.date = this.getGmtDate();
            this.anchor = this.getGmtDate().startOf('month');
        }
    });

    setDefaultDate = action((date) => {
        this.defaultDate = date;
        this.setDate(date);
    });

    setStringDate = action((strDate) => {
        this.setDefaultDate(this.getGmtDate(strDate));
    });

    setDay = action((day) => this.setDate(this.anchor.clone().date(day)));

    onCancel = action(() => {
        if (this.defaultDate) {
            this.setDate(this.defaultDate);
        } else {
            this.setDate(this.getGmtDate());
        }
        this.isCalendarOpen = false;
    });
    onApply = action(() => {
        this.appliedDate = this.date; // then can be observed with parent store
        this.isCalendarOpen = false;
    });

    checkDayAvailability = action((dayTitle) =>
        (!this.allowPastDates) && this.anchor.clone().date(dayTitle).isBefore(this.getGmtDate()));

    checkMonthAvailability = action(() =>
        (!this.allowPastDates) && this.anchor.clone().subtract(1, 'months').isBefore(this.getGmtDate(), 'month'));

    equals = (strDate) => {
        return this.date.isSame(this.getGmtDate(strDate), 'day');
    };

    getGmtDate(date) {
        date = date ? moment.utc(date) : moment.utc();
        return date.hours(0).minutes(0).seconds(0).milliseconds(0);
    }
}
